import { Injectable } from '@angular/core';
import { User } from '../../models/interfaces/user.interface';
import { UserStatus } from '../../models/enums/user-status.enum';
import { environment } from '../../../../environments/environment';
import { UserPayload } from '../../types/payloads/user.payload';
import { createCrudUrl, CrudRequestParams } from '../pagination/models/interfaces/crud';
import { PaginationDto } from '../pagination/models/interfaces/pagination-dto';
import { Api } from '../api/api';
import { getCrudErrors } from '../../utils/functions';
import { userMock } from '../../mocks/interfaces/user.mock';
import { applyMockCrudParams, paginateMockItems } from '../../utils/mock-crud.util';

@Injectable({
  providedIn: 'root'
})
export class UserService {

  private readonly api: Api = new Api();

  private users: User[] = structuredClone(userMock);

  //#region Real API

  public async getMany(crudParams?: CrudRequestParams<User>): Promise<PaginationDto<User>> {
    if (environment.useFakeBackend) {
      return this.getManyMocked(crudParams);
    }

    const url = createCrudUrl<any>(environment.routes.users.base, crudParams);
    const { error, success } = await this.api.getAsync<PaginationDto<User>>(url);

    if (!success)
      throw new Error(getCrudErrors(error)[0]);

    return success;
  }

  public async getOne(id: number): Promise<User | null> {
    if (environment.useFakeBackend) {
      return this.getOneMocked(id);
    }

    const { success } = await this.api.getAsync<User>(environment.routes.users.one(id.toString()));
    return success ?? null;
  }

  public async create(payload: UserPayload): Promise<User | null> {
    if (environment.useFakeBackend) {
      return this.createMocked(payload);
    }

    const { success } = await this.api.postAsync<User>(environment.routes.users.base, payload);
    return success ?? null;
  }

  public async update(id: number, payload: UserPayload): Promise<User | null> {
    if (environment.useFakeBackend) {
      return this.updateMocked(id, payload);
    }

    const { success } = await this.api.putAsync<User>(environment.routes.users.one(id.toString()), payload);
    return success ?? null;
  }

  //#endregion

  //#region Fake Backend

  public async getManyMocked(crudParams: CrudRequestParams<User> = {}): Promise<PaginationDto<User>> {
    const filtered = applyMockCrudParams(this.users, crudParams);
    const page = crudParams.page ?? 1;
    const limit = crudParams.limit ?? (filtered.length || 10);

    return paginateMockItems(filtered, page, limit);
  }

  public async getOneMocked(id: number | string): Promise<User | null> {
    const targetId = String(id);
    return this.users.find(user => String(user.id) === targetId) ?? null;
  }

  public async createMocked(payload: UserPayload): Promise<User | null> {
    const created: User = {
      id: `user-${Date.now()}`,
      tenantId: userMock[0]?.tenantId ?? '',
      tenantName: userMock[0]?.tenantName ?? '',
      name: payload.name ?? '',
      email: payload.email ?? '',
      status: UserStatus.ACTIVE,
      roles: (payload as Partial<User>).roles ?? ['app:*'],
      createdAt: new Date(),
      updatedAt: new Date(),
      isActive: true,
    };

    this.users.unshift(created);
    return created;
  }

  public async updateMocked(id: number | string, payload: UserPayload): Promise<User | null> {
    const index = this.users.findIndex(user => String(user.id) === String(id));

    if (index < 0) {
      return null;
    }

    this.users[index] = {
      ...this.users[index],
      name: payload.name ?? this.users[index].name,
      email: payload.email ?? this.users[index].email,
      roles: (payload as Partial<User>).roles ?? this.users[index].roles,
    };

    return this.users[index];
  }

  public resetMockData(): void {
    this.users = structuredClone(userMock);
  }

  //#endregion

}
